import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Briefcase,
  Plus, 
  Search, 
  Edit,
  Trash2,
  DollarSign,
  ArrowDownToLine,
  ArrowUpFromLine,
  Check,
  X,
  RefreshCw,
  Users,
  Activity,
  Stethoscope,
  Heart,
  Pill
} from 'lucide-react';

// Modos de precificação
const MODO_LABELS = {
  unidade: 'Por unidade',
  mensal: 'Fixo mensal'
};

const Servicos = () => {
  const { api, canViewServiceRevenue, canViewServicePayout, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('catalogo');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('nome_asc'); // P2: Sorting state
  
  // Data states
  const [servicos, setServicos] = useState([]);
  
  // Dialog states
  const [showServicoDialog, setShowServicoDialog] = useState(false);
  const [editingServico, setEditingServico] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [servicoToDelete, setServicoToDelete] = useState(null);
  
  // Form states
  const [servicoForm, setServicoForm] = useState({
    nome: '',
    ativo: true,
    recebido_modo: 'unidade',
    recebido_valor: '',
    pago_modo: 'unidade',
    pago_valor: ''
  });

  // Fetch services
  const fetchServicos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/servicos/catalogo');
      setServicos(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar serviços:', error);
      toast.error('Erro ao carregar catálogo de serviços');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServicos();
  }, []);

  // Filtered and sorted services
  const filteredServicos = useMemo(() => {
    let result = servicos;
    
    if (searchTerm) {
      result = result.filter(s => 
        s.nome?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // P2: Sort results
    const sorted = [...result];
    switch (sortBy) {
      case 'nome_asc':
        return sorted.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
      case 'nome_desc':
        return sorted.sort((a, b) => (b.nome || '').localeCompare(a.nome || '', 'pt-BR'));
      case 'recebido_desc':
        return sorted.sort((a, b) => (b.recebido_valor || 0) - (a.recebido_valor || 0));
      case 'recebido_asc':
        return sorted.sort((a, b) => (a.recebido_valor || 0) - (b.recebido_valor || 0));
      case 'pago_desc':
        return sorted.sort((a, b) => (b.pago_valor || 0) - (a.pago_valor || 0));
      case 'pago_asc':
        return sorted.sort((a, b) => (a.pago_valor || 0) - (b.pago_valor || 0));
      default:
        return sorted;
    }
  }, [servicos, searchTerm, sortBy]);

  // Handle form submit
  const handleSubmitServico = async (e) => {
    e.preventDefault();
    
    if (!servicoForm.nome.trim()) {
      toast.error('Nome do serviço é obrigatório');
      return;
    }

    try {
      const payload = {
        nome: servicoForm.nome.trim().toUpperCase(),
        ativo: servicoForm.ativo,
        recebido_modo: servicoForm.recebido_modo,
        recebido_valor: parseFloat(servicoForm.recebido_valor) || 0,
        pago_modo: servicoForm.pago_modo,
        pago_valor: parseFloat(servicoForm.pago_valor) || 0
      };

      if (editingServico) {
        await api.put(`/servicos/catalogo/${editingServico.id}`, payload);
        toast.success('Serviço atualizado com sucesso');
      } else {
        await api.post('/servicos/catalogo', payload);
        toast.success('Serviço criado com sucesso');
      }

      setShowServicoDialog(false);
      resetServicoForm();
      fetchServicos();
    } catch (error) {
      console.error('Erro ao salvar serviço:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar serviço');
    }
  };

  // Handle delete
  const handleDeleteServico = async () => {
    if (!servicoToDelete) return;

    try {
      await api.delete(`/servicos/catalogo/${servicoToDelete.id}`);
      toast.success('Serviço removido com sucesso');
      setShowDeleteDialog(false);
      setServicoToDelete(null);
      fetchServicos();
    } catch (error) {
      console.error('Erro ao remover serviço:', error);
      toast.error(error.response?.data?.detail || 'Erro ao remover serviço');
    }
  };

  // Reset form
  const resetServicoForm = () => {
    setServicoForm({
      nome: '',
      ativo: true,
      recebido_modo: 'unidade',
      recebido_valor: '',
      pago_modo: 'unidade',
      pago_valor: ''
    });
    setEditingServico(null);
  };

  // Open edit dialog
  const openEditDialog = (servico) => {
    setEditingServico(servico);
    setServicoForm({
      nome: servico.nome || '',
      ativo: servico.ativo !== false,
      recebido_modo: servico.recebido_modo || 'unidade',
      recebido_valor: servico.recebido_valor?.toString() || '',
      pago_modo: servico.pago_modo || 'unidade',
      pago_valor: servico.pago_valor?.toString() || ''
    });
    setShowServicoDialog(true);
  };

  // Open create dialog
  const openCreateDialog = () => {
    resetServicoForm();
    setShowServicoDialog(true);
  };

  // Calculate stats
  const stats = useMemo(() => {
    const ativos = servicos.filter(s => s.ativo !== false).length;
    const inativos = servicos.filter(s => s.ativo === false).length;
    const comPreco = servicos.filter(s => (s.recebido_valor || 0) > 0 || (s.pago_valor || 0) > 0).length;
    return { total: servicos.length, ativos, inativos, comPreco };
  }, [servicos]);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Get service icon
  const getServiceIcon = (nome) => {
    const nameLower = nome?.toLowerCase() || '';
    if (nameLower.includes('fisio')) return <Activity className="h-4 w-4" />;
    if (nameLower.includes('medico')) return <Stethoscope className="h-4 w-4" />;
    if (nameLower.includes('enferma')) return <Heart className="h-4 w-4" />;
    if (nameLower.includes('medica')) return <Pill className="h-4 w-4" />;
    return <Briefcase className="h-4 w-4" />;
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="servicos-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Serviços</h1>
            <p className="text-muted-foreground">Gerencie o catálogo de serviços e precificação</p>
          </div>
          {isAdmin && (
            <Button onClick={openCreateDialog} className="gap-2" data-testid="btn-novo-servico">
              <Plus className="h-4 w-4" />
              Novo Serviço
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Briefcase className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total de Serviços</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.ativos}</p>
                  <p className="text-sm text-muted-foreground">Ativos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 dark:bg-gray-900/30 rounded-lg">
                  <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.inativos}</p>
                  <p className="text-sm text-muted-foreground">Inativos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <DollarSign className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.comPreco}</p>
                  <p className="text-sm text-muted-foreground">Com Preço</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="catalogo" className="gap-2" data-testid="tab-catalogo">
              <Briefcase className="h-4 w-4" />
              Catálogo
            </TabsTrigger>
          </TabsList>

          <TabsContent value="catalogo" className="space-y-4">
            {/* Search and Filter */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar serviços..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="search-servicos"
                    />
                  </div>
                  {/* P2: Sorting dropdown */}
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-44" data-testid="sort-select">
                      <SelectValue placeholder="Ordenar por" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nome_asc">Nome (A–Z)</SelectItem>
                      <SelectItem value="nome_desc">Nome (Z–A)</SelectItem>
                      <SelectItem value="recebido_desc">Recebido (Maior)</SelectItem>
                      <SelectItem value="recebido_asc">Recebido (Menor)</SelectItem>
                      <SelectItem value="pago_desc">Pago (Maior)</SelectItem>
                      <SelectItem value="pago_asc">Pago (Menor)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={fetchServicos} className="gap-2" data-testid="btn-refresh">
                    <RefreshCw className="h-4 w-4" />
                    Atualizar
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Services Table */}
            <Card>
              <CardHeader>
                <CardTitle>Catálogo de Serviços</CardTitle>
                <CardDescription>
                  Lista de todos os serviços disponíveis para contratação
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : filteredServicos.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Briefcase className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Nenhum serviço encontrado</p>
                    {searchTerm && (
                      <p className="text-sm mt-1">Tente ajustar os filtros de busca</p>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Serviço</TableHead>
                          <TableHead>Status</TableHead>
                          {canViewServiceRevenue && (
                            <TableHead className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <ArrowDownToLine className="h-3 w-3 text-green-600" />
                                Valor Recebido
                              </div>
                            </TableHead>
                          )}
                          {canViewServicePayout && (
                            <TableHead className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <ArrowUpFromLine className="h-3 w-3 text-red-600" />
                                Valor Pago
                              </div>
                            </TableHead>
                          )}
                          {isAdmin && <TableHead className="text-center">Ações</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredServicos.map((servico) => (
                          <TableRow key={servico.id} data-testid={`servico-row-${servico.id}`}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-muted rounded-lg">
                                  {getServiceIcon(servico.nome)}
                                </div>
                                <span className="font-medium">{servico.nome}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={servico.ativo !== false ? "default" : "secondary"}>
                                {servico.ativo !== false ? 'Ativo' : 'Inativo'}
                              </Badge>
                            </TableCell>
                            {canViewServiceRevenue && (
                              <TableCell className="text-right">
                                <div>
                                  <span className="font-medium text-green-600 dark:text-green-400">
                                    {formatCurrency(servico.recebido_valor)}
                                  </span>
                                  <p className="text-xs text-muted-foreground">
                                    {MODO_LABELS[servico.recebido_modo] || servico.recebido_modo}
                                  </p>
                                </div>
                              </TableCell>
                            )}
                            {canViewServicePayout && (
                              <TableCell className="text-right">
                                <div>
                                  <span className="font-medium text-red-600 dark:text-red-400">
                                    {formatCurrency(servico.pago_valor)}
                                  </span>
                                  <p className="text-xs text-muted-foreground">
                                    {MODO_LABELS[servico.pago_modo] || servico.pago_modo}
                                  </p>
                                </div>
                              </TableCell>
                            )}
                            {isAdmin && (
                              <TableCell>
                                <div className="flex items-center justify-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => openEditDialog(servico)}
                                    data-testid={`btn-edit-${servico.id}`}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      setServicoToDelete(servico);
                                      setShowDeleteDialog(true);
                                    }}
                                    className="text-destructive hover:text-destructive"
                                    data-testid={`btn-delete-${servico.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create/Edit Service Dialog */}
        <Dialog open={showServicoDialog} onOpenChange={(open) => {
          if (!open) resetServicoForm();
          setShowServicoDialog(open);
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingServico ? 'Editar Serviço' : 'Novo Serviço'}
              </DialogTitle>
              <DialogDescription>
                {editingServico 
                  ? 'Atualize as informações do serviço'
                  : 'Preencha os dados para criar um novo serviço'}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmitServico} className="space-y-6">
              {/* Nome */}
              <div className="space-y-2">
                <Label htmlFor="nome">Nome do Serviço *</Label>
                <Input
                  id="nome"
                  value={servicoForm.nome}
                  onChange={(e) => setServicoForm({...servicoForm, nome: e.target.value})}
                  placeholder="Ex: FISIOTERAPIA"
                  data-testid="input-nome-servico"
                />
              </div>

              {/* Status */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Status</Label>
                  <p className="text-sm text-muted-foreground">
                    Serviços inativos não aparecem nas opções de contratação
                  </p>
                </div>
                <Switch
                  checked={servicoForm.ativo}
                  onCheckedChange={(checked) => setServicoForm({...servicoForm, ativo: checked})}
                  data-testid="switch-ativo"
                />
              </div>

              {/* Valor Recebido (Revenue) - Only for admins */}
              {canViewServiceRevenue && (
                <Card className="border-green-200 dark:border-green-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ArrowDownToLine className="h-4 w-4 text-green-600" />
                      Valor Recebido (Receita)
                    </CardTitle>
                    <CardDescription>
                      Valor cobrado do cliente pelo serviço
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Modo de Cobrança</Label>
                        <Select
                          value={servicoForm.recebido_modo}
                          onValueChange={(value) => setServicoForm({...servicoForm, recebido_modo: value})}
                        >
                          <SelectTrigger data-testid="select-recebido-modo">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unidade">Por unidade</SelectItem>
                            <SelectItem value="mensal">Fixo mensal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Valor (R$)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={servicoForm.recebido_valor}
                          onChange={(e) => setServicoForm({...servicoForm, recebido_valor: e.target.value})}
                          placeholder="0,00"
                          data-testid="input-recebido-valor"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Valor Pago (Cost) - Only for users with payout permission */}
              {canViewServicePayout && (
                <Card className="border-red-200 dark:border-red-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ArrowUpFromLine className="h-4 w-4 text-red-600" />
                      Valor Pago (Custo)
                    </CardTitle>
                    <CardDescription>
                      Valor pago ao profissional pelo serviço
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Modo de Pagamento</Label>
                        <Select
                          value={servicoForm.pago_modo}
                          onValueChange={(value) => setServicoForm({...servicoForm, pago_modo: value})}
                        >
                          <SelectTrigger data-testid="select-pago-modo">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unidade">Por unidade</SelectItem>
                            <SelectItem value="mensal">Fixo mensal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Valor (R$)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={servicoForm.pago_valor}
                        onChange={(e) => setServicoForm({...servicoForm, pago_valor: e.target.value})}
                        placeholder="0,00"
                        data-testid="input-pago-valor"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowServicoDialog(false)}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="btn-salvar-servico">
                  {editingServico ? 'Atualizar' : 'Criar'} Serviço
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar Exclusão</DialogTitle>
              <DialogDescription>
                Tem certeza que deseja remover o serviço "{servicoToDelete?.nome}"?
                Esta ação não pode ser desfeita.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDeleteServico} data-testid="btn-confirmar-delete">
                Remover
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Servicos;
